import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationSelector.css';
import { isWithinServiceArea, getDistanceFromCenter, SERVICE_AREA } from '../../config/serviceArea';

// Fix default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface Location {
  id: string;
  name: string;
  address: string;
  lat?: number;
  lng?: number;
}

interface LocationSelectorProps {
  onLocationSelect: (location: Location) => void;
}

const SAMPLE_LOCATIONS: Location[] = [
  { id: '1', name: 'Downtown', address: '123 Main St', lat: 17.385044, lng: 78.486671 },
  { id: '2', name: 'Midtown', address: '456 Park Ave', lat: 17.445044, lng: 78.386671 },
  { id: '3', name: 'Uptown', address: '789 5th Ave', lat: 17.485044, lng: 78.486671 },
];

// Component to handle map clicks
const LocationMarker: React.FC<{
  position: [number, number];
  setPosition: (pos: [number, number]) => void;
}> = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return <Marker position={position} draggable={true} eventHandlers={{
    dragend: (e) => {
      const marker = e.target;
      const position = marker.getLatLng();
      setPosition([position.lat, position.lng]);
    },
  }} />;
};

const LocationSelector: React.FC<LocationSelectorProps> = ({ onLocationSelect }) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapPosition, setMapPosition] = useState<[number, number]>([17.385044, 78.486671]); // Default Hyderabad
  const [gettingLocation, setGettingLocation] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const handleButtonClick = () => {
    setShowOptions(true);
  };

  const handleUseCurrentLocation = () => {
    setShowOptions(false);
    setGettingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapPosition([latitude, longitude]);
          setGettingLocation(false);
          setShowMapPicker(true);
          
          // Reverse geocode to get address
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
            .then(res => res.json())
            .then(data => {
              setAddressInput(data.display_name || 'Current Location');
            })
            .catch(err => {
              console.error('Reverse geocoding error:', err);
              setAddressInput('Current Location');
            });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please enable location services or enter address manually.');
          setGettingLocation(false);
          setShowMapPicker(true);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      alert('Geolocation is not supported by your browser');
      setGettingLocation(false);
      setShowMapPicker(true);
    }
  };

  const handleAddNewLocation = () => {
    setShowOptions(false);
    setShowMapPicker(true);
  };

  const handleSelect = (location: Location) => {
    setSelectedLocation(location);
    onLocationSelect(location);
    setShowModal(false);
  };

  const handleSearchAddress = async () => {
    if (!addressInput.trim()) return;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressInput)}&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
      alert('Error searching address. Please try again.');
    }
  };

  const handleSelectSearchResult = (result: any) => {
    setMapPosition([parseFloat(result.lat), parseFloat(result.lon)]);
    setAddressInput(result.display_name);
    setSearchResults([]);
  };

  const handleConfirmLocation = () => {
    // Check if location is within service area
    const withinArea = isWithinServiceArea(mapPosition[0], mapPosition[1]);
    
    if (!withinArea) {
      const distance = getDistanceFromCenter(mapPosition[0], mapPosition[1]);
      alert(
        `❌ Sorry! We don't provide services in your location yet.\n\n` +
        `📍 Your location is ${distance.toFixed(1)} km from our service center.\n` +
        `🚀 We currently serve within ${SERVICE_AREA.radius} km of ${SERVICE_AREA.name}.\n\n` +
        `We're expanding soon! Please check back later.`
      );
      return;
    }

    const location: Location = {
      id: 'custom-' + Date.now(),
      name: 'My Location',
      address: addressInput || `${mapPosition[0].toFixed(6)}, ${mapPosition[1].toFixed(6)}`,
      lat: mapPosition[0],
      lng: mapPosition[1]
    };
    setSelectedLocation(location);
    onLocationSelect(location);
    setShowMapPicker(false);
  };

  return (
    <div className="location-selector">
      <label>Select Your Location</label>
      <button
        className="select-button"
        onClick={handleButtonClick}
      >
        {selectedLocation?.name || 'Choose Location'}
      </button>

      {showOptions && (
        <div className="modal-overlay" onClick={() => setShowOptions(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <h2>Choose Location Option</h2>
            <div className="location-options">
              <button className="option-button" onClick={handleUseCurrentLocation} disabled={gettingLocation}>
                {gettingLocation ? '📍 Getting Location...' : '📍 Use Current Location'}
              </button>
              <button className="option-button" onClick={handleAddNewLocation}>
                ➕ Add New Location
              </button>
              <button className="option-button" onClick={() => { setShowOptions(false); setShowModal(true); }}>
                📋 Select Saved Location
              </button>
            </div>
            <button className="close-button" onClick={() => setShowOptions(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Available Locations</h2>
            <div className="locations-list">
              {SAMPLE_LOCATIONS.map((location) => (
                <div
                  key={location.id}
                  className="location-item"
                  onClick={() => handleSelect(location)}
                >
                  <h3>{location.name}</h3>
                  <p>{location.address}</p>
                </div>
              ))}
            </div>
            <button className="close-button" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {showMapPicker && (
        <div className="modal-overlay" onClick={() => setShowMapPicker(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>📍 Select Your Delivery Location</h2>
            
            {/* Address Search */}
            <div className="address-search">
              <input
                type="text"
                placeholder="Search or enter your address..."
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchAddress()}
                className="address-input"
              />
              <button onClick={handleSearchAddress} className="search-button">
                🔍 Search
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="search-result-item"
                    onClick={() => handleSelectSearchResult(result)}
                  >
                    📍 {result.display_name}
                  </div>
                ))}
              </div>
            )}

            {/* Map */}
            <div className="map-container">
              <MapContainer
                center={mapPosition}
                zoom={13}
                style={{ height: '400px', width: '100%', borderRadius: '10px' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {/* Service Area Circle */}
                <Circle
                  center={[SERVICE_AREA.center.lat, SERVICE_AREA.center.lng]}
                  radius={SERVICE_AREA.radius * 1000} // Convert km to meters
                  pathOptions={{
                    color: '#4CAF50',
                    fillColor: '#4CAF50',
                    fillOpacity: 0.1,
                    weight: 2
                  }}
                />
                <LocationMarker position={mapPosition} setPosition={setMapPosition} />
              </MapContainer>
            </div>
            
            <div className="service-area-info">
              🟢 <strong>Service Area:</strong> {SERVICE_AREA.radius} km radius around {SERVICE_AREA.name}
            </div>

            <div className="map-instructions">
              💡 <strong>Tip:</strong> Drag the marker or click on the map to set your exact location
            </div>

            <div className="location-coordinates">
              📍 Selected: {mapPosition[0].toFixed(6)}, {mapPosition[1].toFixed(6)}
            </div>

            <div className="modal-actions">
              <button className="confirm-button" onClick={handleConfirmLocation}>
                ✅ Confirm Location
              </button>
              <button className="close-button" onClick={() => setShowMapPicker(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
